/**
 * Cajones del inventario — fuente ÚNICA de verdad, compartida entre backend
 * (métricas, stock de Aprobaciones/Panel People) y frontend (tabs, badges).
 *
 * Los status de los equipos son texto libre data-driven (InventoryOptionsTable)
 * y los datos reales traen variantes de casing/espacios (ej. 'Localizar '),
 * así que TODA comparación pasa por normalizeDeviceStatus.
 *
 * Tres cajones (Sol 15/09/2026):
 *   - activo:     Entregado + Lista para Entregar (el parque que está en manos
 *                 de alguien o listo para entregarse)
 *   - intermedio: el taller — Localizar, Pending: Revisar, Pending: Formateo,
 *                 Pending: Para Reparar, En proceso de recupero
 *   - inactivo:   bajas (lista CERRADA, decisión Sol 20/08)
 * Cualquier status nuevo/desconocido cae en ACTIVO por default — nada
 * desaparece por accidente. OJO: acá se agrupan estados, NUNCA se renombran —
 * 'Entregado', 'Lista para Entregar' y 'BAJA TOTAL' se comparan por literal en
 * office-manager, MCP tools y el cron semanal.
 */

export type DeviceBucket = 'active' | 'intermediate' | 'inactive';

export const INACTIVE_DEVICE_STATUSES = [
  'baja total',
  'bloqueada',
  'donada',
  'vendida a clapper',
  'vintage',
  'robada',
] as const;

export const INTERMEDIATE_DEVICE_STATUSES = [
  'localizar',
  'pending: revisar',
  'pending: formateo',
  'pending: para reparar',
  'en proceso de recupero',
] as const;

export const READY_TO_DELIVER_STATUS = 'lista para entregar';

export function normalizeDeviceStatus(status?: string | null): string {
  return (status ?? '').trim().toLowerCase();
}

export function deviceBucket(status?: string | null): DeviceBucket {
  const s = normalizeDeviceStatus(status);
  if ((INACTIVE_DEVICE_STATUSES as readonly string[]).includes(s)) return 'inactive';
  if ((INTERMEDIATE_DEVICE_STATUSES as readonly string[]).includes(s)) return 'intermediate';
  return 'active';
}

/**
 * "No está dado de baja": activo O intermedio. Es lo que usan el stock por
 * clapbase (Aprobaciones / Panel People: un equipo en formateo cuenta como
 * "en preparación") y la confirmación al cruzar a inactivo. Para el split de
 * tres cajones usá deviceBucket().
 */
export function isDeviceActive(status?: string | null): boolean {
  return deviceBucket(status) !== 'inactive';
}

/**
 * Un equipo "Lista para Entregar" con owner @winclap ya está ASIGNADO a alguien
 * (lo eligió Aprobaciones o el Panel People y espera la entrega); sin owner o
 * con "Oficina" todavía no tiene destinatario. Misma regla que el stock de
 * Aprobaciones (Sol 04/09).
 */
export function hasAssignedOwner(owner?: string | null): boolean {
  return (owner ?? '').toLowerCase().includes('@winclap.com');
}

export function isReadyToDeliver(status?: string | null): boolean {
  return normalizeDeviceStatus(status) === READY_TO_DELIVER_STATUS;
}
